import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import { withRouter } from 'react-router-dom';
import { FileTextOutlined, PaperClipOutlined, DownOutlined } from '@ant-design/icons';
import { Button, Drawer, Modal, Menu, Dropdown } from 'antd';
import EditClaim from "../../components/EditClaim/EditClaim";
import NewClaim from "../../components/NewClaim/NewClaim";
import classes from './Claims.module.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTools, faScroll } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../../components/DataTable/DataTable';
import DocumentList from '../../components/DocumentList/DocumentList';
import StringFormatter from '../../shared/stringFormatter';
import NoteList from '../../components/NoteList/NoteList';
import { isObjectNotEmpty, isStringNotEmpty } from '../../shared/objectUtils';
import { selectListIsLoading, selectListPagination, selectListRecords } from '../../store/utility';

const stringFormatter = new StringFormatter();

const Claims = (props) => {
    //#region constants

    const fullWidth = global.window.innerWidth;
    const singularEntityName = "Claim";
    const pluralEntityName = "Claims";
    const filter = "ALL";

    //#endregion
    //#region useDispatch and useSelectors

    const dispatch = useDispatch();
    const records = useSelector(state => selectListRecords(state.claims.lists, filter));
    const isLoading = useSelector(state => selectListIsLoading(state.claims.lists, filter));
    const pagination = useSelector(state => selectListPagination(state.claims.lists, filter));

    //#endregion
    //#region useStates

    const [selectedRecord, setSelectedRecord] = useState({});
    const [showEditEntity, setShowEditEntity] = useState(false);
    const [showNewEntity, setShowNewEntity] = useState(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);

    //#endregion
    //#region toggles

    const toggleEditEntity = () => {
        setShowEditEntity(!showEditEntity);
    };

    const toggleNewEntity = () => {
        setShowNewEntity(!showNewEntity);
    };

    const toggleDocumentsModal = () => {
        setShowDocumentsModal(!showDocumentsModal);
    };

    const toggleNotesModal = () => {
        setShowNotesModal(!showNotesModal);
    };

    //#endregion
    //#region goTos

    const goToClaim = (id) => {
        props.history.push({ pathname: `/claims/${id}` }, {
            previousPageTitle: "Claims",
            previousPageLocation: props.location,
            previousBreadcrumbs: props.breadcrumbs,
        });
    };

    //#endregion
    //#region table methods

    const refreshTable = () => {
        let searchParams = {
            page: 1,
            size: 5,
            isDeleted: false
        };

        dispatch(actionCreators.fetchClaimList(filter, searchParams));
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const pager = { ...pagination };
        pager.current = pagination.current;
        dispatch(actionCreators.fetchClaimListSuccess(filter, { params: { pagination: pager } }));

        let searchParams = {
            page: pagination.current,
            size: pagination.pageSize,
            isDeleted: false
        };

        if (isStringNotEmpty(sorter.order)) {
            const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
            searchParams.order = sortOrder;
        }
        if (isStringNotEmpty(sorter.field)) {
            searchParams.sort = sorter.field;
        }

        dispatch(actionCreators.fetchClaimList(filter, searchParams));
    };

    //#endregion
    //#region table displays

    const menu = (record) => {
        return (
            <Menu>
                <Menu.Item key="manageDocuments" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleDocumentsModal(); }}>
                    <PaperClipOutlined style={{ marginRight: 8 }} />
                    <span>Manage Documents</span>
                </Menu.Item>
                <Menu.Item key="manageNotes" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleNotesModal(); }}>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    <span>Manage Notes</span>
                </Menu.Item>
                <Menu.Item key="editEntity" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleEditEntity(); }}>
                    <FontAwesomeIcon className="anticon" icon={faScroll} style={{ marginRight: 8 }} />
                    <span>Edit {singularEntityName} Details</span>
                </Menu.Item>
                <Menu.Item key="goToDashboard" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); goToClaim(record.id); }}>
                    <span>Go to {singularEntityName} Dashboard</span>
                </Menu.Item>
            </Menu>
        );
    };

    const columns = [
        {
            title: <FontAwesomeIcon icon={faTools} size="lg" />,
            key: 'actions',
            className: classes.columnNoBreak,
            width: 50,
            render: (text, record) => {
                return (
                    <Dropdown overlay={menu(record)} trigger={['click']}>
                        <Button>Actions <DownOutlined /></Button>
                    </Dropdown>
                );
            },
            align: 'center',
        },
        {
            title: 'Claim ID',
            dataIndex: 'irisId',
            key: 'irisId',
        },
        {
            title: 'Claim Status',
            dataIndex: 'claimStatus',
            key: 'claimStatus',
            render: (text, record) => { return stringFormatter.toFormattedString("ClaimStatus", text, null); },
        },
        {
            title: 'Is Against Carrier',
            dataIndex: 'isAgainstCarrier',
            key: 'isAgainstCarrier',
            render: (text, record) => { return stringFormatter.toYesNo(text); },
        },
        {
            title: 'Is Against Shipper',
            dataIndex: 'isAgainstShipper',
            key: 'isAgainstShipper',
            render: (text, record) => { return stringFormatter.toYesNo(text); },
        },
        {
            title: 'Is Claim Against Insurance',
            dataIndex: 'isInsuranceClaim',
            key: 'isInsuranceClaim',
            render: (text, record) => { return stringFormatter.toYesNo(text); },
        },
        {
            title: 'Claim Against',
            dataIndex: ['account', 'name'],
            key: 'account.name',
        },
        {
            title: 'Comments',
            dataIndex: 'comments',
            key: 'comments',
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            render: (text, record) => { return stringFormatter.toFormattedString("UnitPackagingCount", text, record.unitPackaging); },
        },
        {
            title: 'BOL Status',
            dataIndex: 'bolStatus',
            key: 'bolStatus',
            render: (text, record) => { return stringFormatter.toFormattedString("BOLStatus", text, null); },
        },
        {
            title: 'Load ID',
            dataIndex: 'irisLoadId',
            key: 'irisLoadId',
        },
        {
            title: 'Adjustment Amount',
            dataIndex: 'adjustment',
            key: 'adjustment',
            render: (text, record) => { return stringFormatter.toFormattedMoney(text); },
        },
        {
            title: 'BOL #',
            dataIndex: 'bolNumber',
            key: 'bolNumber',
        }
    ];

    //#endregion
    //#region displays

    const newEntityComponents = (
        <Drawer
            title={"New " + singularEntityName}
            onClose={toggleNewEntity}
            visible={showNewEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <NewClaim
                cancel={toggleNewEntity}
            />
        </Drawer>
    );

    const editEntityComponents = (
        <Drawer
            title={"Edit " + singularEntityName}
            onClose={toggleEditEntity}
            visible={showEditEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <EditClaim
                cancel={toggleEditEntity}
                claim={selectedRecord}
            />
        </Drawer>
    );

    const documentsComponent = (
        <Modal
            title="Claim Documents"
            visible={showDocumentsModal === true}
            width="100%"
            style={{ top: 0 }}
            onCancel={toggleDocumentsModal}
            footer={null}
            zIndex={1000}
            destroyOnClose={true}
            maskClosable={false}
        >
            {isObjectNotEmpty(selectedRecord) ? (
                <DocumentList
                    documentEntityType="CLAIM"
                    documentEntityId={selectedRecord.id}
                />
            ) : null}
        </Modal>
    );

    const notesComponent = (
        <Modal
            title="Claim Notes"
            visible={showNotesModal === true}
            width="100%"
            style={{ top: 0 }}
            onCancel={toggleNotesModal}
            footer={null}
            zIndex={1000}
            destroyOnClose={true}
            maskClosable={false}
        >
            {isObjectNotEmpty(selectedRecord) ? (
                <NoteList
                    noteEntityType="CLAIM"
                    noteEntityId={selectedRecord.id}
                />
            ) : null}
        </Modal>
    );

    //#endregion
    //#region useEffects

    useEffect(() => {
        refreshTable();
    }, []);

    //#endregion

    return (
        <DataTable
            dataSource={records}
            columns={columns}
            pagination={pagination}
            onChange={handleTableChange}
            loading={isLoading === true}
            rowClassName={classes.dataTableRow}
            singularEntityName={singularEntityName}
            pluralEntityName={pluralEntityName}
            newEntityAction={toggleNewEntity}
            rowKey={record => record.id}
            style={{ backgroundColor: '#ffffff' }}
        >
            {newEntityComponents}
            {editEntityComponents}
            {documentsComponent}
            {notesComponent}
        </DataTable>
    );
};

export default withRouter(Claims);